import { LogEntry, Car } from './supabase';

/**
 * Calculates estimated current mileage based on log entries history
 */
export function estimateCurrentMileage(car: Car, logEntries: LogEntry[]): number {
  const logsWithMileage = (logEntries || [])
    .filter(l => l.mileage !== null && l.mileage > 0)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Fallback 1: If no logs, use the car's stored mileage (odometer)
  if (logsWithMileage.length === 0) return car.mileage || 0;

  const latestLog = logsWithMileage[0];
  const latestMileage = Number(latestLog.mileage);
  const latestDate = new Date(latestLog.date);
  const now = new Date();

  // If the log is from today or the future, no need to estimate
  if (latestDate >= now) return latestMileage;

  // Fallback 2: Not enough data for a rate, just return latest known
  if (logsWithMileage.length < 2) {
      return latestMileage;
  }

  // Calculate rate from logs (odometer to odometer)
  const oldestLog = logsWithMileage[logsWithMileage.length - 1];
  const timeDiff = latestDate.getTime() - new Date(oldestLog.date).getTime();
  const distDiff = latestMileage - Number(oldestLog.mileage);
  
  if (timeDiff <= 0 || distDiff <= 0) return latestMileage;

  const dailyRate = distDiff / (timeDiff / (1000 * 60 * 60 * 24));
  const daysSinceLastLog = (now.getTime() - latestDate.getTime()) / (1000 * 60 * 60 * 24);

  // Add the estimated distance since the last log to the latest odometer reading
  return Math.round(latestMileage + (daysSinceLastLog * dailyRate));
}

/**
 * Calculates estimated mileage at a specific date, using known logs to interpolate or extrapolate.
 * Useful for historical cost-per-km calculations.
 */
export function estimateMileageAtDate(
  purchaseMileage: number | null | undefined, 
  boughtAt: string | null | undefined, 
  logEntries: any[], 
  estimatedYearlyKm: number | null | undefined, 
  targetDate: Date
): number {
  const targetMs = targetDate.getTime();
  
  // Sort logs chronologically
  const sortedLogs = [...logEntries]
    .filter(l => typeof l.mileage === 'number' && l.mileage > 0)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // 1. If targetDate is before or exactly at the first known mileage
  if (sortedLogs.length === 0) {
    if (purchaseMileage != null && boughtAt != null) {
      const boughtMs = new Date(boughtAt).getTime();
      const defaultDailyRate = (estimatedYearlyKm || 15000) / 365.25;
      const daysDiff = (targetMs - boughtMs) / (1000 * 60 * 60 * 24);
      return Math.max(0, purchaseMileage + Math.round(daysDiff * defaultDailyRate));
    }
    return purchaseMileage || 0;
  }

  // Find the closest logs before and after the targetDate
  let lastLogBefore: any = null;
  let firstLogAfter: any = null;

  for (const log of sortedLogs) {
    const logTime = new Date(log.date).getTime();
    if (logTime <= targetMs) {
      lastLogBefore = log;
    } else if (!firstLogAfter) {
      firstLogAfter = log;
      break;
    }
  }

  // 2. We have a log exactly at or after, and one before (Interpolation)
  if (lastLogBefore && firstLogAfter) {
    const timeBefore = new Date(lastLogBefore.date).getTime();
    const timeAfter = new Date(firstLogAfter.date).getTime();
    const mileageBefore = lastLogBefore.mileage as number;
    const mileageAfter = firstLogAfter.mileage as number;

    if (timeAfter === timeBefore) return mileageBefore; // Avoid division by zero
    
    const ratio = (targetMs - timeBefore) / (timeAfter - timeBefore);
    return Math.round(mileageBefore + (mileageAfter - mileageBefore) * ratio);
  }

  // 3. Extrapolation forward (targetDate is after all known logs)
  if (lastLogBefore && !firstLogAfter) {
    // Need a rate. Try to use overall rate from purchase to lastLog, or from first log to last log
    let rateDaily = (estimatedYearlyKm || 15000) / 365.25; // fallback
    
    if (sortedLogs.length >= 2) {
      // Find average daily rate over the last X logs or total
      const first = sortedLogs[0];
      const last = sortedLogs[sortedLogs.length - 1];
      const timeDiff = new Date(last.date).getTime() - new Date(first.date).getTime();
      const distDiff = last.mileage - first.mileage;
      
      if (timeDiff > 0 && distDiff >= 0) {
        rateDaily = distDiff / (timeDiff / (1000 * 60 * 60 * 24));
      }
    } else if (purchaseMileage != null && boughtAt != null) {
      const boughtMs = new Date(boughtAt).getTime();
      const logMs = new Date(lastLogBefore.date).getTime();
      if (logMs > boughtMs) {
         rateDaily = (lastLogBefore.mileage - purchaseMileage) / ((logMs - boughtMs) / (1000 * 60 * 60 * 24));
      }
    }

    const daysSinceLastLog = (targetMs - new Date(lastLogBefore.date).getTime()) / (1000 * 60 * 60 * 24);
    return Math.round(lastLogBefore.mileage + Math.max(0, daysSinceLastLog * rateDaily));
  }

  // 4. Extrapolation backwards (targetDate is before all known logs)
  if (!lastLogBefore && firstLogAfter) {
    let rateDaily = (estimatedYearlyKm || 15000) / 365.25; // fallback
    // We can use purchase mileage
    if (purchaseMileage != null && boughtAt != null) {
       const boughtMs = new Date(boughtAt).getTime();
       const logMs = new Date(firstLogAfter.date).getTime();
       if (logMs > boughtMs && targetMs >= boughtMs) {
           const fullRatio = (targetMs - boughtMs) / (logMs - boughtMs);
           return Math.round(purchaseMileage + (firstLogAfter.mileage - purchaseMileage) * fullRatio);
       }
    }
    
    const daysBefore = (new Date(firstLogAfter.date).getTime() - targetMs) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.round(firstLogAfter.mileage - daysBefore * rateDaily));
  }

  return 0; // Fallback
}

/**
 * Estimates the date when a specific mileage target will be reached
 */
export function estimateDateAtMileage(car: Car, logEntries: any[], targetMileage: number): Date | null {
  const logsWithMileage = (logEntries || [])
    .filter(l => l.mileage !== null && l.mileage > 0)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (logsWithMileage.length === 0) return null;

  const latestLog = logsWithMileage[0];
  const latestMileage = Number(latestLog.mileage);
  
  if (targetMileage <= latestMileage) return new Date(latestLog.date);

  // Need a rate
  let dailyRate = 0;
  if (logsWithMileage.length >= 2) {
    const oldestLog = logsWithMileage[logsWithMileage.length - 1];
    const timeDiff = new Date(latestLog.date).getTime() - new Date(oldestLog.date).getTime();
    const distDiff = latestMileage - Number(oldestLog.mileage);
    if (timeDiff > 0 && distDiff > 0) {
      dailyRate = distDiff / (timeDiff / (1000 * 60 * 60 * 24));
    }
  }

  // Fallback to estimated yearly km if no rate or low rate
  if (dailyRate <= 0) {
    dailyRate = (car.ai_lifespan_km ? 15000 : 15000) / 365.25; // Default 15k
  }

  const distToCover = targetMileage - latestMileage;
  const daysPlanned = distToCover / dailyRate;
  
  const targetDate = new Date(latestLog.date);
  targetDate.setDate(targetDate.getDate() + Math.round(daysPlanned));
  
  return targetDate;
}
